import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import classNames from 'classnames'
import { useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { MdKeyboardArrowRight, MdClose } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import type { ChangeEvent, ReactElement } from 'react'
import type { IApiError } from '~/types/api'
import type { ICatalogCategory, ILokaliseKey } from '~/types/catalog'

import NewCategoryForm from './NewCategoryForm'
import LokaliseKeyInput from './NewCategoryForm/LokaliseKeyInput'

import moveIcon from '~/assets/move-icon.svg'
import Button from '~/components/Buttons/Button'
import CheckboxInput from '~/components/Inputs/Basic/CheckboxInput'
import ModalDialog from '~/components/ModalDialog'
import { AUTHORIZATIONS, FORM_ERRORS, TOAST_ERRORS } from '~/const'
import { useIsUserAuthorized } from '~/providers/Authorizations'
import {
  keys as catalogKeys,
  useDeleteCategoryMutation,
  useEnableCategoryMutation,
  useUpdateCategoryMutation,
} from '~/queries/catalog'
import { handleGenericError } from '~/utils/error'
import { stopPropagation } from '~/utils/event'
import { displayToast } from '~/utils/toast'

import './styles.scss'

interface FormValues {
  lokaliseKey: string
}

const validationSchema = Yup.object().shape({
  lokaliseKey: Yup.string().required(FORM_ERRORS.REQUIRED),
})

interface Props {
  category: ICatalogCategory
  className?: string
  handleResetNewElement: VoidFunction
  isAddingElement: boolean
  isMatchParams?: boolean
  isNewElement: boolean
  lokaliseKeys: ILokaliseKey[]
  onExpand: VoidFunction
}

const CatalogCategory = ({
  category,
  className,
  handleResetNewElement,
  isAddingElement,
  isMatchParams,
  isNewElement,
  lokaliseKeys,
  onExpand,
}: Props): ReactElement => {
  /** Local state */

  const isUserWriteAuthorized = useIsUserAuthorized([AUTHORIZATIONS.CCC.CATALOG.WRITE])

  const navigate = useNavigate()

  const queryClient = useQueryClient()

  const componentClasses = classNames('catalog-category-component', className, {
    'catalog-category-component--active': isMatchParams,
    'catalog-category-component--adding-element': isAddingElement && !isNewElement,
  })

  const chevronIconClasses = classNames(
    'catalog-category-component__main-container__actions-container__toggle-wrapper__chevron-icon',
    {
      'catalog-category-component__main-container__actions-container__toggle-wrapper__chevron-icon--open':
        isMatchParams,
    }
  )

  const { category_name, enabled, lokalise_key, nb_translations } = category

  const defaultValues: FormValues = {
    lokaliseKey: lokalise_key,
  }

  const methods = useForm<FormValues>({
    defaultValues,
    resolver: yupResolver(validationSchema),
  })

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
  } = methods

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)

  const [nbTranslations, setNbTranslations] = useState(nb_translations)

  /** Queries & Mutations */

  const { mutateAsync: updateCategory } = useUpdateCategoryMutation()

  const { mutateAsync: deleteCategory } = useDeleteCategoryMutation()

  const { mutateAsync: enableCategory } = useEnableCategoryMutation()

  /** Handlers */

  const handleCheckboxChange = async (event: ChangeEvent<HTMLInputElement>) => {
    stopPropagation(event)

    try {
      await enableCategory({ category_name, enabled: event.target.checked })
      await queryClient.invalidateQueries({ queryKey: catalogKeys.categories() })
    } catch (e) {
      handleGenericError(e)
    }
  }

  const handleFormSubmit = async ({ lokaliseKey }: FormValues) => {
    if (!isUserWriteAuthorized) {
      displayToast(TOAST_ERRORS.NOT_AUTHORIZED, 'error')
      return
    }

    if (lokaliseKey) {
      try {
        await updateCategory({ category_name, lokalise_key: lokaliseKey })
        await queryClient.invalidateQueries({ queryKey: catalogKeys.categories() })
        displayToast('Category successfully updated', 'success')
      } catch (e) {
        captureException(e)

        if (isAxiosError(e)) {
          const errorData = e.response?.data as IApiError

          switch (errorData.kind) {
            case 'not_found_in_lokalise':
              displayToast('Lokalise key could not be found', 'error')
              break
            default:
              displayToast('Category could not be updated', 'error')
              break
          }
        }
      }
    }
  }

  const handleDeleteModalState = (isOpen: boolean) => () => setIsDeleteModalOpen(isOpen)

  const handleDeleteConfirm = async () => {
    if (!isUserWriteAuthorized) {
      displayToast(TOAST_ERRORS.NOT_AUTHORIZED, 'error')
      return
    }

    try {
      await deleteCategory({ category_name })
      await queryClient.invalidateQueries({ queryKey: catalogKeys.categories() })
      displayToast('Category successfully deleted', 'success')
      setIsDeleteModalOpen(false)
      navigate('/catalog/procolor/categories', { replace: true })
    } catch (e) {
      captureException(e)
      displayToast('Category could not be deleted', 'error')
    }
  }

  const handleNbTranslationsChange = (nbTranslations: number) => setNbTranslations(nbTranslations)

  /** Render */

  return (
    <>
      <div className={componentClasses}>
        <div className="catalog-category-component__main-container" onClick={onExpand}>
          <div className="catalog-category-component__main-container__icon-id-container">
            <div
              className="catalog-category-component__main-container__icon-id-container__icon-wrapper move-button"
              onClick={stopPropagation}
            >
              <img
                className="catalog-category-component__main-container__icon-id-container__icon-wrapper__move-icon"
                src={moveIcon}
              />
            </div>
            {isNewElement ? (
              <NewCategoryForm lokaliseKeys={lokaliseKeys} onResetNewElement={handleResetNewElement} />
            ) : (
              <p className="catalog-category-component__main-container__icon-id-container__id" title={category_name}>
                {category_name}
              </p>
            )}
          </div>
          {!isNewElement ? (
            <div className="catalog-category-component__main-container__actions-container">
              <CheckboxInput
                defaultChecked={enabled}
                disabled={isAddingElement}
                label="Active"
                onChange={handleCheckboxChange}
                onClick={stopPropagation}
              />
              <div className="catalog-category-component__main-container__actions-container__toggle-wrapper">
                <MdKeyboardArrowRight className={chevronIconClasses} color="#ff5301" />
              </div>
            </div>
          ) : null}
        </div>
        {isMatchParams && !isNewElement ? (
          <div className="catalog-category-component__edit-menu">
            <div className="catalog-category-component__edit-menu__delete-action-container">
              <p className="catalog-category-component__edit-menu__delete-action-container__translations-count">
                {nbTranslations ? `${nbTranslations} translations` : null}
              </p>
              <Button
                className="catalog-category-component__edit-menu__delete-action-container__delete-button"
                disabled={isAddingElement}
                onClick={handleDeleteModalState(true)}
                type="button"
              >
                <MdClose color="#fff" size={14} />
                Delete
              </Button>
            </div>
            <FormProvider {...methods}>
              <form
                className="catalog-category-component__edit-menu__actions-container"
                onSubmit={handleSubmit(handleFormSubmit)}
              >
                <LokaliseKeyInput
                  disabled={isAddingElement || lokaliseKeys.length === 0}
                  lokaliseKeys={lokaliseKeys}
                  name="lokaliseKey"
                  onNbTranslationsChange={handleNbTranslationsChange}
                />
                <Button
                  className="catalog-category-component__edit-menu__actions-container__save-button"
                  disabled={isSubmitting || !isDirty}
                  outlined
                  type="submit"
                >
                  Save
                </Button>
              </form>
            </FormProvider>
          </div>
        ) : null}
      </div>
      <ModalDialog
        isOpen={isDeleteModalOpen}
        mainText="Are you sure you want to delete this category?"
        onClose={handleDeleteModalState(false)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}

export default CatalogCategory
